import React, { useRef } from 'react';

// Gradient direction to SVG coordinates
const GRADIENT_COORDS = {
  down: { x1: '0%', y1: '0%', x2: '0%', y2: '100%' },
  up: { x1: '0%', y1: '100%', x2: '0%', y2: '0%' },
  left: { x1: '100%', y1: '0%', x2: '0%', y2: '0%' },
  right: { x1: '0%', y1: '0%', x2: '100%', y2: '0%' }
};

// Softness mask coordinates
const SOFTNESS_COORDS = {
  horizontal: { x1: '0%', y1: '0%', x2: '100%', y2: '0%' }
};

// Simple ID counter for unique gradient IDs
let idCounter = 0;
function generateId() {
  return `divider-${++idCounter}`;
}

/**
 * Hook to generate SVG gradient definitions for divider presets.
 * Color is inherited via CSS `currentColor` from the nearest ancestor element.
 * @param {object} options - Gradient options
 * @param {number} options.fadeOpacity - Fade opacity percentage (0-100)
 * @param {string} options.fadeDirection - Direction of fade gradient
 * @param {number} options.softness - Edge softness percentage (0-100)
 * @returns {object} Gradient configuration with fill, mask, and defs
 */
export function useDividerGradient({ fadeOpacity = 0, fadeDirection = 'down', softness = 0 }) {
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = generateId();
  }
  const baseId = idRef.current;
  const gradientId = `grad-${baseId}`;
  const softnessId = `soft-${baseId}`;

  const hasGradient = fadeOpacity > 0;
  const hasSoftness = softness > 0;

  const coords = GRADIENT_COORDS[fadeDirection] || GRADIENT_COORDS.down;
  const startOpacity = 1 - fadeOpacity / 100;

  // Softness creates a mask that fades edges
  const softStart = softness / 2;
  const softEnd = 100 - softness / 2;

  let defs = null;
  if (hasGradient || hasSoftness) {
    defs = React.createElement(
      'defs',
      null,
      hasGradient &&
        React.createElement(
          'linearGradient',
          { id: gradientId, x1: coords.x1, y1: coords.y1, x2: coords.x2, y2: coords.y2 },
          React.createElement('stop', {
            offset: '0%',
            stopColor: 'currentColor',
            stopOpacity: startOpacity
          }),
          React.createElement('stop', {
            offset: '100%',
            stopColor: 'currentColor',
            stopOpacity: '1'
          })
        ),
      hasSoftness && [
        React.createElement(
          'linearGradient',
          {
            key: 'softgrad',
            id: `${softnessId}-grad`,
            x1: SOFTNESS_COORDS.horizontal.x1,
            y1: SOFTNESS_COORDS.horizontal.y1,
            x2: SOFTNESS_COORDS.horizontal.x2,
            y2: SOFTNESS_COORDS.horizontal.y2
          },
          React.createElement('stop', { offset: '0%', stopColor: 'white', stopOpacity: '0' }),
          React.createElement('stop', {
            offset: `${softStart}%`,
            stopColor: 'white',
            stopOpacity: '1'
          }),
          React.createElement('stop', {
            offset: `${softEnd}%`,
            stopColor: 'white',
            stopOpacity: '1'
          }),
          React.createElement('stop', { offset: '100%', stopColor: 'white', stopOpacity: '0' })
        ),
        React.createElement(
          'mask',
          { key: 'softmask', id: softnessId },
          React.createElement('rect', {
            width: '100%',
            height: '100%',
            fill: `url(#${softnessId}-grad)`
          })
        )
      ]
    );
  }

  return {
    gradientId,
    softnessId,
    defs,
    fill: hasGradient ? `url(#${gradientId})` : 'currentColor',
    mask: hasSoftness ? `url(#${softnessId})` : null
  };
}
